/** Aphelion mark — theme-aware (strokes/fill follow --logo-fg).
 *
 *  `animated` adds the dotted outer orbit and the planet travelling it.
 *
 *  SIZE-AWARE GEOMETRY. The viewBox is 400 units wide, so a planet of r=16
 *  renders ~3px across in a 46px app-bar mark — technically present, visually
 *  nothing. Rather than a second component or a magic `compact` flag, the
 *  planet and the orbit strokes scale UP as the render gets smaller, so the
 *  mark reads at 46px and is unchanged at the 210px the login page uses.
 */
export function AphelionLogo({
  width = 42,
  height = 27,
  animated = false,
}: {
  width?: number;
  height?: number;
  animated?: boolean;
}) {
  // 1 at hero size, rising for small marks. The upper clamp is GEOMETRY, not
  // taste: the planet travels an ellipse of ry=85 and the orange core has
  // r=55, so at the top of its path the planet's edge sits at 85 - 16k from
  // centre. Anything above k = 1.875 puts that inside the core and the planet
  // visibly clips through it. 1.75 leaves ~2 units of daylight.
  const k = Math.min(1.75, Math.max(1, 150 / Math.max(width, 1)));

  return (
    <svg viewBox="-200 -130 400 260" width={width} height={height} role="img" aria-label="Aphelion Energy">
      <circle cx="0" cy="0" r="55" fill="#FF6B00" />
      <g transform="rotate(-20)">
        <ellipse
          className="logo-orbit-solid"
          cx="0" cy="0" rx="130" ry="65" fill="none"
          strokeWidth={animated ? 6 * k : 10}
        />
        {animated && (
          <>
            <ellipse
              className="logo-orbit-dotted"
              cx="0" cy="0" rx="170" ry="85" fill="none"
              strokeWidth={3 * k}
              strokeDasharray={`${12 * k},${10 * k}`}
            />
            <circle className="logo-planet logo-planet-anim" r={16 * k}>
              <animateMotion
                dur="8s"
                repeatCount="indefinite"
                path="M170,0 A170,85 0 1,1 -170,0 A170,85 0 1,1 170,0"
              />
            </circle>
          </>
        )}
      </g>
    </svg>
  );
}
